import { CircularProgress, CircularProgressLabel, Collapse } from '@chakra-ui/react';
import { ChevronDownIcon } from '@sketchnote/icons';
import { Avatar } from '@sketchnote/shared-components';
import React, { useState } from 'react';
import { Column } from 'src/features/kanban/component/Column/types';
import { TaskT } from 'src/features/tasks/types/tasks';
import { getFormattedValue } from 'src/utils/getFormattedValue';
const KanbanColumnList = ({
  children,
  data: { name, color, count },
}: {
  children: JSX.Element | JSX.Element[];
  data: {
    name: string;
    color: string;
    count: number;
  };
}) => {
  const [collapse, setCollapse] = useState(false);

  return (
    <div className="w-full no-scrollbar overflow-auto">
      <div className="w-full mt-8">
        <div
          role="button"
          className="w-full flex items-center justify-between pb-10 border-b"
          onClick={() => {
            setCollapse(!collapse);
          }}
        >
          <div className="flex">
            <span
              style={{
                transform: collapse ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'all 0.5s',
              }}
              className={'mt-4 block'}
            >
              <ChevronDownIcon />
            </span>
            <span
              style={{ backgroundColor: color }}
              className="px-8 py-2 rounded-sm font-medium text-xs flex items-center"
            >
              {name} ({count})
            </span>
          </div>
        </div>
      </div>
      <div className="w-full overflow-auto no-scrollbar">
        <Collapse in={collapse} className="overflow-auto no-scrollbar" style={{ overflow: 'auto' }}>
          <div className="mt-8">{children}</div>
        </Collapse>
      </div>
    </div>
  );
};

const KanbanItemList = ({ item }: { item: TaskT }) => {
  return (
    <div className="border border-gray-200 rounded-sm px-12 py-8 mb-8">
      <p className="text-xs font-medium">🛠&nbsp;&nbsp;{getFormattedValue(item.name)}</p>
    </div>
  );
};

export const AssigneeChart = ({
  data,
  assignee,
  tasks,
}: {
  data: Column[];
  assignee:
    | {
        firstname: string;
        avatar: string;
        memberId: string;
        lastname: string;
        email: string;
      }
    | undefined;
  tasks: TaskT[];
}) => {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((task) => task.completed).length;
  const incompleteTasks = tasks.filter((task) => !task.completed).length;

  const percentageCompleted = totalTasks < 1 ? 0 : Math.round((completedTasks / totalTasks) * 100);

  return (
    <div className="bg-white rounded-sm shadow-sm p-16 mr-32">
      <div className="border-b border-gray-200 pb-12 px-4 mb-16 flex items-center">
        <Avatar
          name={assignee?.firstname || ''}
          type="photo"
          src={assignee?.avatar}
          wrapperClassName="mr-16"
        />
        <p className="font-medium text-gray-600">
          {assignee?.firstname} {assignee?.lastname}
        </p>
      </div>
      <div className="border-b border-gray-200 pb-12 flex items-center">
        <div className="w-2/4">
          <div className="flex justify-between text-xs mb-8 font-medium text-gray-500">
            <p>Total tasks</p>
            <p>{totalTasks}</p>
          </div>
          <div className="flex justify-between text-xs mb-8 font-medium text-gray-500">
            <p>Completed tasks</p>
            <p>{completedTasks}</p>
          </div>
          <div className="flex justify-between text-xs mb-8 font-medium text-gray-500">
            <p>Incomplete tasks</p>
            <p>{incompleteTasks}</p>
          </div>
        </div>
        <div className="w-2/4 flex justify-center">
          <CircularProgress value={percentageCompleted} color="blue.600" size="60px">
            <CircularProgressLabel className="text-gray-600">
              {percentageCompleted}%
            </CircularProgressLabel>
          </CircularProgress>
        </div>
      </div>
      <div>
        {data.map((column) => (
          <KanbanColumnList
            key={column.id}
            data={{
              name: column?.name,
              color: column?.color,
              count: column.items.filter((item) =>
                assignee?.memberId === ''
                  ? (item.assignee?.length || 0) < 1
                  : item.assignee?.some((member) => member.memberId === assignee?.memberId)
              ).length,
            }}
          >
            <>
              {column?.items
                .filter((item) =>
                  assignee?.memberId === ''
                    ? (item.assignee?.length || 0) < 1
                    : item.assignee?.some((member) => member.memberId === assignee?.memberId)
                )
                .map((item) => (
                  <KanbanItemList key={item.id} item={item} />
                ))}
            </>
          </KanbanColumnList>
        ))}
      </div>
    </div>
  );
};
